import React from 'react'
import './Misc.scss'
import AppHeader from 'components/AppHeader'

export default () => {
    const domain = window.__auth__.domain;
    const tenant = window.__auth__.domain.replace(".auth0.com", "");

    return <div>
        <AppHeader hideWidgets={true}/>

        <div className="misc-header" style={{marginTop: 60}}>
            <h1>Identity federation with NetApp Cloud Central</h1>
        </div>

        <div className="misc-instructions" style={{padding: "0 80px"}}>
            <p>Identity federation enables you to manage access to your hybrid cloud resources centrally. With identity federation, you can use single sign-on to access your NetApp Cloud Central accounts using credentials from your corporate directory. Identity federation uses open standards, such as Security Assertion Markup Language 2.0 (SAML) and OpenID Connect (OIDC).</p>
            <p style={{margin: "15px 0 0 0"}}>Currently, we support identity federation with Active Directory Federation Services (ADFS) and Microsoft Azure Active Directory (AD). In the future we plan to add support for third-party SAMLP Identity Providers.</p>

            <h3 style={{margin: "20px 0 0 0"}}>Table of contents</h3>
            <ul>
                <li><a href="#federationSupportAdfs">Setting up identity federation with ADFS</a></li>
                <li><a href="#federationSupportAzureAd">Setting up identity federation with Microsoft Azure AD</a></li>
                <li><a href="#federationSupportSaml">Setting up identity federation with a SAML identity provider</a></li>
            </ul>

            <section id="federationSupportAdfs" className="anchor">
                <h3 style={{margin: "20px 0 0 0"}}>Setting up identity federation with ADFS</h3>

                <h4 style={{margin: "20px 0 13px 0"}}>
                    Before you begin
                </h4>
                <ul style={{marginLeft: 45}}>
                    <li>You should have an existing ADFS server.</li>
                    <li>You should have permission to configure the ADFS server to enable identity federation with our authentication and authorization service provider (Auth0).</li>
                </ul>
                <h4 style={{margin: "15px 0 0 0"}}>
                    Steps
                </h4>
                <ol>
                    <li>Sign up on <a href="/">NetApp Cloud Central</a> and verify your email address after you receive a confirmation email.</li>
                    <li>
                        <p>Contact the NetApp Cloud Central team using the chat icon available in the lower-right of Cloud Central, or by emailing us at <a href="mailto:saas.support@netapp.com">saas.support@netapp.com</a>. To set up your ADFS integration, we will use the company name that you entered when signing up and the email domain from your email address.</p>
                    </li>
                    <li>
                        <p>
                            Configure your ADFS server to enable identity federation with our authentication provider (domain: https://{domain}).
                        </p>

                        <p>
                            Instructions and an explanation for configuring the ADFS server can be found here <a href="https://auth0.com/docs/connections/enterprise/adfs">https://auth0.com/docs/connections/enterprise/adfs</a>
                        </p>

                        <p>You can configure the ADFS server automatically by using the following script:</p>
                        <div>
                        <pre>
                            <code className="language-powershell">
                                (<span className="built_in">new-object</span>{" Net.WebClient -property @{Encoding = [Text.Encoding]::UTF8}).DownloadString"}(<span className="string">"https://raw.github.com/auth0/adfs-auth0/master/adfs.ps1"</span>) | iex <br/>
                        AddRelyingParty <span className="string">"urn:auth0:{tenant}"</span> <span className="string">"https://{domain}/login/callback"</span>
                            </code>
                        </pre>
                        </div>

                        <p>If you choose to set up the ADFS configuration manually, follow <a href="https://auth0.com/docs/connections/enterprise/adfs#manual-setup">these instruction</a> and use the following values:</p>
                        <ul style={{marginLeft: 45}}>
                            <li>YOUR_TENANT: <span className="code">{tenant}</span></li>
                            <li>YOUR_AUTH0_DOMAIN: <span className="code">{domain}</span></li>
                        </ul>
                    </li>

                    <li>
                        Once your ADFS is configured, contact us so that we can enable the connection on our end. We will need either the ADFS URL, if the ADFS is accessible from the internet (preferred), or the federation metadata xml file. Note: If you provide the metadata file, then the file must be updated before each expiry period as defined in the file itself.
                    </li>

                    <li>
                        <p>Log in to NetApp Cloud Central services using your corporate credentials.</p>
                        <p>When you type your email address in the email field, Cloud Central automatically detects your company's connection. You can then provide your corporate credentials to complete the login.</p>
                    </li>
                </ol>
            </section>
            <section id="federationSupportAzureAd" className="anchor">
                <h3 style={{margin: "20px 0 0 0"}}>Setting up identity federation with Microsoft Azure AD</h3>

                <h4 style={{margin: "20px 0 13px 0"}}>
                    Before you begin
                </h4>
                <ul style={{marginLeft: 45}}>
                    <li>You should have set up Microsoft Azure Active Directory</li>
                    <li>You should have permission to create a new application in your Azure Active Directory</li>
                </ul>
                <h4 style={{margin: "15px 0 0 0"}}>
                    Steps
                </h4>
                <ol>
                    <li>Sign up on <a href="/">NetApp Cloud Central</a> and verify your email address after you receive a confirmation email.</li>
                    <li>
                        <p>Contact the NetApp Cloud Central team using the chat icon available in the lower-right of Cloud Central, or by emailing us at <a href="mailto:saas.support@netapp.com">saas.support@netapp.com</a>. To set up your Azure Active Directory integration, we will use the company name that you entered when signing up and the email domain from your email address.</p>
                    </li>
                    <li>
                        <p>
                            Create a new application in Azure Active Directory that will be configured to allow connections from Cloud Central's authentication provider.
                        </p>

                        <p>
                            Instructions on creating the application can be found here <a
                            href="https://auth0.com/docs/connections/enterprise/azure-active-directory/v2">https://auth0.com/docs/connections/enterprise/azure-active-directory/v2</a>
                        </p>

                        <p>
                            Copy the value of the authentication key. You need to send us the value before we can enable
                            identity federation. Note that Auth0 refers to this key value as the Client Secret.
                        </p>

                        <p>When setting up the application and its permissions, use the following values:</p>
                        <ul style={{marginLeft: 45}}>
                            <li>Sign-on URL: <span className="code">{window.location.origin}</span></li>
                            <li>Reply URL: <span className="code">https://{domain}/login/callback</span></li>
                        </ul>
                    </li>

                    <li>
                        <p>Once your Azure Active Directory is configured, contact us so that we can enable the connection on our end. We will need the following values that you can get from your Active Directory application's configuration:</p>
                        <ul style={{marginLeft: 45}}>
                            <li>Application ID</li>
                            <li>Application Secret</li>
                            <li>Microsoft Azure AD Domain</li>
                        </ul>
                    </li>

                    <li>
                        <p>Log in to NetApp Cloud Central services using your corporate credentials.</p>
                        <p>When you type your email address in the email field, Cloud Central automatically detects your company's connection and redirects you to your Microsoft Azure Active Directory login page.</p>
                    </li>
                </ol>
            </section>
            <section id="federationSupportSaml" className="anchor">
                <h3 style={{margin: "20px 0 0 0"}}>Setting up identity federation with a SAML identity provider</h3>

                <h4 style={{margin: "20px 0 13px 0"}}>
                    Before you begin
                </h4>
                <ul style={{marginLeft: 45}}>
                    <li>You should have an existing SAML identity provider (IdP).</li>
                    <li>You should have the required access to configure a new connection with your SAML IdP.</li>
                </ul>
                <h4 style={{margin: "15px 0 0 0"}}>
                    Steps
                </h4>
                <ol>
                    <li>Sign up on <a href="/">NetApp Cloud Central</a> and verify your email address after you receive a confirmation email.</li>

                    <li>
                        <p>
                            Create a new connection with your SAML IdP. This procedure varies depending on your SAML IdP. The following are links to documentation for several prevalent providers, as well as <a href="https://auth0.com/docs/protocols/saml/saml-sp-generic">generic instructions</a> if your identity provider does not appear in the list:
                        </p>

                        <ul style={{marginLeft: 45}}>
                            <li><a href="https://auth0.com/docs/adfs" target="_blank" rel="noopener noreferrer">ADFS</a></li>
                            <li><a href="https://auth0.com/docs/okta" target="_blank" rel="noopener noreferrer">Okta</a></li>
                            <li><a href="https://auth0.com/docs/onelogin" target="_blank" rel="noopener noreferrer">OneLogin</a></li>
                            <li><a href="https://auth0.com/docs/ping7" target="_blank" rel="noopener noreferrer">Ping7</a></li>
                            <li><a href="https://auth0.com/docs/saml/identity-providers/salesforce" target="_blank" rel="noopener noreferrer">SalesForce</a></li>
                            <li><a href="https://auth0.com/docs/siteminder" target="_blank" rel="noopener noreferrer">SiteMinder</a></li>
                            <li><a href="https://auth0.com/docs/ssocircle" target="_blank" rel="noopener noreferrer">SSOCircle</a></li>
                        </ul>

                        <p>
                            Disregard the Auth0 configurations in the documentation, as this part will be handled by the Cloud Central Development Team. You need to set up the IdP side only. Cloud Central uses Auth0 as a SAML service provider for federated login.
                        </p>
                    </li>

                    <li>
                        <p>Make sure your SAML idP have Attribute Statement "email" with the user email. The SAML response should include:</p>
                        <pre>
                            <code className="language-xml">
                                <span>&lt;saml:AttributeStatement <span className="attribute-name">xmlns:x500</span>=<span className="attribute-value">"urn:oasis:names:tc:SAML:2.0:profiles:attribute:X500"</span> <span className="attribute-name">xmlns:xs</span>=<span className="attribute-value">"http://www.w3.org/2001/XMLSchema"</span> <span className="attribute-name">xmlns:xsi</span>=<span className="attribute-value">"http://www.w3.org/2001/XMLSchema-instance"</span>&gt;</span><br/>
                                <span>&nbsp;&nbsp;&nbsp;&nbsp;&lt;saml:Attribute <span className="attribute-name">Name</span>=<span className="attribute-value">"email"</span> <span className="attribute-name">NameFormat</span>=<span className="attribute-value">"urn:oasis:names:tc:SAML:1.1:nameid-format:X509SubjectName"</span>&gt;</span><br/>
                                <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;saml:AttributeValue <span className="attribute-name">xsi:type</span>=<span className="attribute-value">"xs:string"</span>&gt;<span className="text">email@domain.com</span>&lt;/saml:AttributeValue&gt;</span><br/>
                                <span>&nbsp;&nbsp;&nbsp;&nbsp;&lt;/saml:Attribute&gt;</span><br/>
                                <span>&lt;/saml:AttributeStatement&gt;</span><br/>
                            </code>
                        </pre>
                    </li>

                    <li>
                        <p>Once you have configured the identity provider section of the documentation, contact us (using the chat icon available in the lower-right of Cloud Central, or by emailing us at <a href="mailto:saas.support@netapp.com">saas.support@netapp.com</a>) so that we can create the connection on our end. We will need the following values (there may be slight variations depending on your SAML IdP):</p>
                        <ul style={{marginLeft: 45}}>
                            <li>Sign In URL</li>
                            <li>X509 Signing Certificate</li>
                            <li>Sign Out URL</li>
                        </ul>
                    </li>

                    <li>
                        <p>After you contact us, we will finalize the connection’s configuration with Auth0. We will then supply you with the connection metadata required to finish setting up the connection with your IdP.</p>

                    </li>

                    <li>
                        <p>Log in to NetApp Cloud Central services using your corporate credentials.</p>
                        <p>When you type your email address in the email field, Cloud Central automatically detects your company's connection and redirects you to your SAML login page.</p>
                    </li>
                </ol>
            </section>
        </div>

    </div>
}